/**
 * Semántica de estados del dominio: convierte los enums del backend en etiquetas en
 * español y en clases de Tailwind de una "píldora" de estado (fondo suave + texto +
 * borde + punto), tal como se ve en la imagen de referencia.
 *
 * Los enums vienen de `backend/prisma/schema.prisma`:
 * - `EstadoOrden` (módulo 7): PENDIENTE_CONFIRMACION → CONFIRMADA → EN_PREPARACION →
 *   ENVIADA → ENTREGADA / CANCELADA.
 * - `EstadoLead` (módulo 9): FRIO / TIBIO / CALIENTE / CLIENTE.
 * - `Role` (módulo 1): ADMIN / AGENTE_HUMANO.
 * - `EstadoPlantilla` (módulo 8): el estado REAL en Meta (BORRADOR hasta que se envía; de ahí en
 *   adelante lo que devuelva el WABA o el webhook `message_template_status_update`).
 *
 * Se usan en las pantallas que están por venir (Órdenes, CRM, Equipo) para que todos
 * pinten el mismo estado con el mismo color.
 */

import type { EstadoLead, EstadoOrden, EstadoPlantilla, Role } from '@/types/domain';

/** Tonos disponibles. Se mapean a las clases de abajo (siempre literales: Tailwind no puede leer strings dinámicos). */
export type ToneName = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'brand';

export interface ToneStyle {
  /** Clases para una píldora: `className={cx('inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-micro font-semibold', tone.className)}` */
  className: string;
  /** Clase del punto indicador. */
  dot: string;
  /** Clase del texto suelto (sin fondo), ej. un delta "+2.24%". */
  text: string;
}

export const tones: Record<ToneName, ToneStyle> = {
  neutral: {
    className: 'border-line bg-surface-muted text-ink-muted',
    dot: 'bg-ink-faint',
    text: 'text-ink-muted',
  },
  brand: {
    className: 'border-brand-200 bg-brand-50 text-brand-700',
    dot: 'bg-brand-600',
    text: 'text-brand-700',
  },
  info: {
    className: 'border-brand-200 bg-info-soft text-brand-700',
    dot: 'bg-info',
    text: 'text-brand-700',
  },
  success: {
    className: 'border-success/20 bg-success-soft text-success-strong',
    dot: 'bg-success',
    text: 'text-success-strong',
  },
  warning: {
    className: 'border-warning/25 bg-warning-soft text-warning-strong',
    dot: 'bg-warning',
    text: 'text-warning-strong',
  },
  danger: {
    className: 'border-danger/20 bg-danger-soft text-danger-strong',
    dot: 'bg-danger',
    text: 'text-danger-strong',
  },
};

export interface StatusMeta<T extends string> {
  label: string;
  tone: ToneName;
  value: T;
}

/** Estados de una orden (módulo 7) con su tono en la interfaz. */
export const orderStatus: Record<EstadoOrden, StatusMeta<EstadoOrden>> = {
  PENDIENTE_CONFIRMACION: { value: 'PENDIENTE_CONFIRMACION', label: 'Pendiente de confirmación', tone: 'warning' },
  CONFIRMADA: { value: 'CONFIRMADA', label: 'Confirmada', tone: 'info' },
  EN_PREPARACION: { value: 'EN_PREPARACION', label: 'En preparación', tone: 'brand' },
  ENVIADA: { value: 'ENVIADA', label: 'Enviada', tone: 'brand' },
  ENTREGADA: { value: 'ENTREGADA', label: 'Entregada', tone: 'success' },
  CANCELADA: { value: 'CANCELADA', label: 'Cancelada', tone: 'danger' },
};

/** Orden del flujo de una orden (para el stepper de estados). */
export const orderStatusFlow: EstadoOrden[] = [
  'PENDIENTE_CONFIRMACION',
  'CONFIRMADA',
  'EN_PREPARACION',
  'ENVIADA',
  'ENTREGADA',
];

/** Estado del lead (módulo 9 / kanban del CRM). */
export const leadStatus: Record<EstadoLead, StatusMeta<EstadoLead>> = {
  FRIO: { value: 'FRIO', label: 'Frío', tone: 'neutral' },
  TIBIO: { value: 'TIBIO', label: 'Tibio', tone: 'info' },
  CALIENTE: { value: 'CALIENTE', label: 'Caliente', tone: 'warning' },
  CLIENTE: { value: 'CLIENTE', label: 'Cliente', tone: 'success' },
};

/** Columnas del kanban del CRM, en orden. */
export const leadStatusOrder: EstadoLead[] = ['FRIO', 'TIBIO', 'CALIENTE', 'CLIENTE'];

/**
 * Estado de una plantilla de WhatsApp (módulo 8).
 *
 * `BORRADOR` es el único estado en el que la plantilla se puede editar desde el dashboard (todavía no
 * existe en el WABA); `APROBADA` + `activo` es la combinación que la vuelve «enviable» (el flag que
 * calcula el backend). `RECHAZADA`, `PAUSADA` y `DESHABILITADA` son las que Meta bloquea: se ven en
 * rojo/ámbar para que el admin las corrija en WhatsApp Manager.
 */
export const templateStatus: Record<EstadoPlantilla, StatusMeta<EstadoPlantilla>> = {
  BORRADOR: { value: 'BORRADOR', label: 'Borrador', tone: 'neutral' },
  PENDIENTE: { value: 'PENDIENTE', label: 'Pendiente de revisión', tone: 'warning' },
  APROBADA: { value: 'APROBADA', label: 'Aprobada', tone: 'success' },
  RECHAZADA: { value: 'RECHAZADA', label: 'Rechazada', tone: 'danger' },
  PAUSADA: { value: 'PAUSADA', label: 'Pausada', tone: 'warning' },
  DESHABILITADA: { value: 'DESHABILITADA', label: 'Deshabilitada', tone: 'danger' },
  ARCHIVADA: { value: 'ARCHIVADA', label: 'Archivada', tone: 'neutral' },
  DELETADA: { value: 'DELETADA', label: 'Borrada en Meta', tone: 'danger' },
};

/** Roles dentro de un tenant (módulo 1). */
export const roleLabel: Record<Role, string> = {
  ADMIN: 'Administrador',
  AGENTE_HUMANO: 'Agente humano',
};
